import { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { FaUser, FaCog, FaBell, FaChartLine, FaClock, FaSync, FaDownload } from "react-icons/fa";
import { useUserRecentActivity } from "../hooks/useAuditLogs";
import auditLogService from "../services/auditLogService";
import { getUserPreferences, saveUserPreferences } from "../utils/userPreferences";
import styles from "./MyDashboard.module.css";

const MyDashboard = () => {
    const { isDarkMode } = useTheme();
    const isAuthenticated = useIsAuthenticated();
    const { accounts } = useMsal();
    const [activeTab, setActiveTab] = useState('overview');
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showInstallButton, setShowInstallButton] = useState(false);
    
    // Get user information from MSAL
    const user = accounts.length > 0 ? accounts[0] : null;
    const userOid = user?.localAccountId || user?.homeAccountId;
    const username = user?.username;

    // Load user preferences from localStorage
    const [settings, setSettings] = useState(() => getUserPreferences(username));

    // Fetch user's recent activity from audit logs (last 50 events)
    const { recentActivity, loading: activityLoading, error: activityError, refresh } = useUserRecentActivity(userOid, 50);

    useEffect(() => {
        document.title = 'My Dashboard - WetLab LIMS';

        // Check if app can be installed
        const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
        if (!isInstalled) {
            const handleBeforeInstallPrompt = (e) => {
                e.preventDefault();
                setDeferredPrompt(e);
                setShowInstallButton(true);
            };

            window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

            return () => {
                window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            };
        }
    }, []);

    // Save preferences whenever settings change
    useEffect(() => {
        if (username) {
            saveUserPreferences(username, settings);
        }
    }, [settings, username]);

    const handleSettingChange = (setting) => {
        setSettings(prev => ({
            ...prev,
            [setting]: !prev[setting]
        }));
    };

    const getActionBadgeClass = (action) => {
        switch (action) {
            case 'CREATE':
                return styles.badgeCreate;
            case 'UPDATE':
                return styles.badgeUpdate;
            case 'DELETE':
                return styles.badgeDelete;
            default:
                return styles.badgeDefault;
        }
    };

    const getResultClass = (result) => {
        return result === 'SUCCESS' ? styles.statusSuccess : styles.statusFailure;
    };

    const handleInstallApp = async () => {
        if (!deferredPrompt) {
            return;
        }

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            setShowInstallButton(false);
        }

        setDeferredPrompt(null);
    };

    const renderOverview = () => (
        <div className={styles.overview}>
            <div className={styles.welcomeSection}>
                <h2>Welcome back, {user?.name || user?.username || 'User'}!</h2>
                <p>Here's what's happening in your lab today.</p>
            </div>

            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <h3>Active Projects</h3>
                    <div className={styles.statNumber}>12</div>
                    <div className={styles.statLabel}>Currently in progress</div>
                </div>
                <div className={styles.statCard}>
                    <h3>Pending Tasks</h3>
                    <div className={styles.statNumber}>5</div>
                    <div className={styles.statLabel}>Require your attention</div>
                </div>
                <div className={styles.statCard}>
                    <h3>Notifications</h3>
                    <div className={styles.statNumber}>3</div>
                    <div className={styles.statLabel}>New alerts</div>
                </div>
            </div>

            <div className={styles.quickActions}>
                <h3>Quick Actions</h3>
                <div className={styles.actionGrid}>
                    <button 
                        className={styles.actionButton}
                        onClick={() => setActiveTab('profile')}
                    >
                        <span className={styles.actionIcon}><FaUser /></span>
                        <span>My Profile</span>
                    </button>
                    <button 
                        className={styles.actionButton}
                        onClick={() => setActiveTab('settings')}
                    >
                        <span className={styles.actionIcon}><FaCog /></span>
                        <span>Settings</span>
                    </button>
                    {showInstallButton && (
                        <button 
                            className={styles.actionButton}
                            onClick={handleInstallApp}
                        >
                            <span className={styles.actionIcon}><FaDownload /></span>
                            <span>Install App</span>
                        </button>
                    )}
                </div>
            </div>

            <div className={styles.recentActivity}>
                <div className={styles.activityHeader}>
                    <h3>Recent Activity</h3>
                    <button 
                        className={styles.refreshButton}
                        onClick={refresh}
                        disabled={activityLoading}
                        title="Refresh activity"
                    >
                        <FaSync className={activityLoading ? styles.spinning : ''} />
                    </button>
                </div>
                
                {activityLoading && (
                    <div className={styles.loadingMessage}>
                        <div className={styles.spinner}></div>
                        Loading your recent activity...
                    </div>
                )}

                {activityError && (
                    <div className={styles.errorMessage}>
                        {activityError}
                    </div>
                )}

                {!activityLoading && !activityError && recentActivity.length === 0 && (
                    <div className={styles.emptyState}>
                        <p>No recent activity to display.</p>
                    </div>
                )}

                {!activityLoading && !activityError && recentActivity.length > 0 && (
                    <div className={styles.activityList}>
                        {recentActivity.map((log) => (
                            <div key={log.auditId} className={styles.activityItem}>
                                <div className={styles.activityMeta}>
                                    <span className={styles.activityTime}>
                                        {auditLogService.formatTimeAgo(log.tsUtc)}
                                    </span>
                                    <span className={`${styles.badge} ${getActionBadgeClass(log.action)}`}>
                                        {log.action}
                                    </span>
                                    {log.result && (
                                        <span className={getResultClass(log.result)}>
                                            {log.result}
                                        </span>
                                    )}
                                </div>
                                <span className={styles.activityText}>
                                    {log.summaryText || `${log.action} ${log.resourceType} ${log.resourceId || ''}`}
                                </span>
                                {log.resourceType && (
                                    <span className={styles.activityResource}>
                                        {log.resourceType}
                                        {log.resourceId ? ` #${log.resourceId}` : ''}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderProfile = () => (
        <div className={styles.profileSection}>
            <h2>Profile Information</h2>
            <div className={styles.profileForm}>
                <div className={styles.avatarSection}>
                    <div className={styles.avatar}>
                        <FaUser />
                    </div>
                    <button className={styles.changeAvatarBtn}>Change Photo</button>
                </div>
                
                <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                        <label>Full Name</label>
                        <input type="text" defaultValue={user?.name || user?.username || "John Doe"} />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Email</label>
                        <input type="email" defaultValue={user?.username || "john.doe@company.com"} />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Department</label>
                        <input type="text" defaultValue="Laboratory Analysis" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Phone</label>
                        <input type="tel" defaultValue="+1 (555) 123-4567" />
                    </div>
                </div>
                
                <div className={styles.formActions}>
                    <button className={styles.saveBtn}>Save Changes</button>
                    <button className={styles.cancelBtn}>Cancel</button>
                </div>
            </div>
        </div>
    );

    const renderSettings = () => (
        <div className={styles.settingsSection}>
            <h2>Settings</h2>
            
            <div className={styles.settingsGroup}>
                <h3>Notifications</h3>
                <div className={styles.settingsList}>
                    <div className={styles.settingItem}>
                        <div className={styles.settingInfo}>
                            <span>Email Notifications</span>
                            <small>Receive important updates via email</small>
                        </div>
                        <label className={styles.toggle}>
                            <input 
                                type="checkbox" 
                                checked={settings.emailNotifications}
                                onChange={() => handleSettingChange('emailNotifications')}
                            />
                            <span className={styles.slider}></span>
                        </label>
                    </div>
                    <div className={styles.settingItem}>
                        <div className={styles.settingInfo}>
                            <span>Push Notifications</span>
                            <small>Get real-time notifications in your browser</small>
                        </div>
                        <label className={styles.toggle}>
                            <input 
                                type="checkbox" 
                                checked={settings.pushNotifications}
                                onChange={() => handleSettingChange('pushNotifications')}
                            />
                            <span className={styles.slider}></span>
                        </label>
                    </div>
                </div>
            </div>

            <div className={styles.settingsGroup}>
                <h3>Preferences</h3>
                <div className={styles.settingsList}>
                    <div className={styles.settingItem}>
                        <div className={styles.settingInfo}>
                            <span>Default View</span>
                            <small>Choose your preferred landing page</small>
                        </div>
                        <select 
                            value={settings.defaultView}
                            onChange={(e) => setSettings(prev => ({ ...prev, defaultView: e.target.value }))}
                            className={styles.selectInput}
                        >
                            <option value="home">Home</option>
                            <option value="dashboard">My Dashboard</option>
                            <option value="samples">Sample Control</option>
                            <option value="analysis">Analysis</option>
                            <option value="qa">Quality Assurance</option>
                            <option value="clients">Client Management</option>
                            <option value="configurations">Configurations</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderHelpSection = () => (
        <div className={styles.placeholderSection}>
            <h2>Help & Support</h2>
            <p>Get help with using the WetLab LIMS system and contact support.</p>
            <button 
                className={styles.backButton}
                onClick={() => setActiveTab('overview')}
            >
                ← Back to Dashboard
            </button>
        </div>
    );

    const renderPlaceholderSection = (title, description) => (
        <div className={styles.placeholderSection}>
            <h2>{title}</h2>
            <p>{description}</p>
            <button 
                className={styles.backButton}
                onClick={() => setActiveTab('overview')}
            >
                ← Back to Dashboard
            </button>
        </div>
    );

    return (
        <div className={styles.myDashboard}>
            <div className={styles.header}>
                <h1>My Dashboard</h1>
                <nav className={styles.breadcrumb}>
                    <button 
                        className={`${styles.breadcrumbItem} ${activeTab === 'overview' ? styles.active : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        Dashboard
                    </button>
                    {activeTab !== 'overview' && (
                        <>
                            <span className={styles.breadcrumbSeparator}>›</span>
                            <span className={styles.breadcrumbItem}>
                                {activeTab === 'profile' && 'My Profile'}
                                {activeTab === 'settings' && 'Settings'}
                            </span>
                        </>
                    )}
                </nav>
            </div>

            <div className={styles.content}>
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'profile' && renderProfile()}
                {activeTab === 'settings' && renderSettings()}
            </div>
        </div>
    );
};

export default MyDashboard;
